import React from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { 
  MapPin, Briefcase, DollarSign, Clock, Users, Building2,
  Laptop, Landmark, Heart, GraduationCap, ShoppingBag, Factory,
  Plane, Truck, Utensils, Lightbulb, Radio, Shield, Hammer,
  Palette, Leaf, Banknote, Stethoscope, Scale, Film
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Industry icon mapping with colors
const getIndustryConfig = (industry) => {
  const industryLower = (industry || '').toLowerCase();
  
  if (industryLower.includes('technology') || industryLower.includes('it ') || industryLower.includes('software') || industryLower.includes('consulting')) {
    return { icon: Laptop, bgFrom: 'from-blue-100', bgTo: 'to-blue-200', iconColor: 'text-blue-600' };
  }
  if (industryLower.includes('banking') || industryLower.includes('finance') || industryLower.includes('fintech')) {
    return { icon: Landmark, bgFrom: 'from-green-100', bgTo: 'to-green-200', iconColor: 'text-green-600' };
  }
  if (industryLower.includes('healthcare') || industryLower.includes('hospital') || industryLower.includes('medical') || industryLower.includes('pharma')) {
    return { icon: Stethoscope, bgFrom: 'from-red-100', bgTo: 'to-red-200', iconColor: 'text-red-600' };
  }
  if (industryLower.includes('education') || industryLower.includes('edtech') || industryLower.includes('training')) {
    return { icon: GraduationCap, bgFrom: 'from-yellow-100', bgTo: 'to-yellow-200', iconColor: 'text-yellow-600' };
  }
  if (industryLower.includes('retail') || industryLower.includes('ecommerce') || industryLower.includes('e-commerce') || industryLower.includes('fmcg')) {
    return { icon: ShoppingBag, bgFrom: 'from-pink-100', bgTo: 'to-pink-200', iconColor: 'text-pink-600' };
  }
  if (industryLower.includes('manufacturing') || industryLower.includes('industrial') || industryLower.includes('automotive')) {
    return { icon: Factory, bgFrom: 'from-gray-100', bgTo: 'to-gray-200', iconColor: 'text-gray-600' };
  }
  if (industryLower.includes('travel') || industryLower.includes('aviation') || industryLower.includes('hospitality') || industryLower.includes('tourism')) {
    return { icon: Plane, bgFrom: 'from-cyan-100', bgTo: 'to-cyan-200', iconColor: 'text-cyan-600' };
  }
  if (industryLower.includes('logistics') || industryLower.includes('supply chain') || industryLower.includes('transport')) {
    return { icon: Truck, bgFrom: 'from-orange-100', bgTo: 'to-orange-200', iconColor: 'text-orange-600' };
  }
  if (industryLower.includes('food') || industryLower.includes('restaurant') || industryLower.includes('beverage')) {
    return { icon: Utensils, bgFrom: 'from-amber-100', bgTo: 'to-amber-200', iconColor: 'text-amber-600' };
  }
  if (industryLower.includes('energy') || industryLower.includes('power') || industryLower.includes('oil') || industryLower.includes('gas')) {
    return { icon: Lightbulb, bgFrom: 'from-yellow-100', bgTo: 'to-yellow-200', iconColor: 'text-yellow-500' };
  }
  if (industryLower.includes('media') || industryLower.includes('entertainment') || industryLower.includes('broadcast')) {
    return { icon: Film, bgFrom: 'from-purple-100', bgTo: 'to-purple-200', iconColor: 'text-purple-600' };
  }
  if (industryLower.includes('telecom') || industryLower.includes('communication')) {
    return { icon: Radio, bgFrom: 'from-indigo-100', bgTo: 'to-indigo-200', iconColor: 'text-indigo-600' };
  }
  if (industryLower.includes('insurance') || industryLower.includes('security')) {
    return { icon: Shield, bgFrom: 'from-teal-100', bgTo: 'to-teal-200', iconColor: 'text-teal-600' };
  }
  if (industryLower.includes('construction') || industryLower.includes('real estate') || industryLower.includes('infrastructure')) {
    return { icon: Hammer, bgFrom: 'from-stone-100', bgTo: 'to-stone-200', iconColor: 'text-stone-600' };
  }
  if (industryLower.includes('design') || industryLower.includes('creative') || industryLower.includes('advertising') || industryLower.includes('marketing')) {
    return { icon: Palette, bgFrom: 'from-fuchsia-100', bgTo: 'to-fuchsia-200', iconColor: 'text-fuchsia-600' };
  }
  if (industryLower.includes('agriculture') || industryLower.includes('farming') || industryLower.includes('agri')) {
    return { icon: Leaf, bgFrom: 'from-lime-100', bgTo: 'to-lime-200', iconColor: 'text-lime-600' };
  }
  if (industryLower.includes('legal') || industryLower.includes('law')) {
    return { icon: Scale, bgFrom: 'from-slate-100', bgTo: 'to-slate-200', iconColor: 'text-slate-600' };
  }
  if (industryLower.includes('ngo') || industryLower.includes('non-profit') || industryLower.includes('charity')) {
    return { icon: Heart, bgFrom: 'from-rose-100', bgTo: 'to-rose-200', iconColor: 'text-rose-600' };
  }
  
  // Default
  return { icon: Building2, bgFrom: 'from-purple-100', bgTo: 'to-purple-200', iconColor: 'text-purple-600' };
};

const JobCard = ({ job }) => {
  const navigate = useNavigate();
  const industry = job.industryType || job.industry || 'IT Services & Consulting';
  const { icon: IndustryIcon, bgFrom, bgTo, iconColor } = getIndustryConfig(industry);

  const handleCardClick = (e) => {
    // Don't navigate if clicking on Apply button
    if (e.target.closest('button')) return;
    navigate(`/job/${job.id}`);
  };

  return (
    <Card 
      className="p-5 hover:shadow-lg transition-all duration-300 border border-gray-200 hover:border-[#4a90e2] cursor-pointer group"
      onClick={handleCardClick}
      data-testid={`job-card-${job.id}`}
    >
      <div className="flex gap-4">
        {/* Industry Icon */}
        <div className="flex-shrink-0">
          <div className={`w-14 h-14 bg-gradient-to-br ${bgFrom} ${bgTo} rounded flex items-center justify-center overflow-hidden`}>
            <IndustryIcon className={`w-7 h-7 ${iconColor}`} />
          </div>
        </div>

        {/* Job Details */}
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-800 group-hover:text-[#4a90e2] transition-colors mb-1">
            {job.title}
          </h3>
          {/* Show Industry instead of Company Name */}
          <p className={`font-medium mb-3 ${iconColor}`}>
            {industry}
          </p>

          <div className="flex flex-wrap gap-3 mb-3">
            <div className="flex items-center text-sm text-gray-600">
              <Briefcase className="w-4 h-4 mr-1.5 text-gray-500" />
              {job.experience}
            </div>
            <div className="flex items-center text-sm text-gray-600">
              <DollarSign className="w-4 h-4 mr-1.5 text-gray-500" />
              {job.salary}
            </div>
            <div className="flex items-center text-sm text-gray-600">
              <MapPin className="w-4 h-4 mr-1.5 text-gray-500" />
              {job.location}
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-3">
            {job.skills.map((skill, index) => (
              <Badge key={index} variant="secondary" className="bg-blue-50 text-blue-700 hover:bg-blue-100">
                {skill}
              </Badge>
            ))}
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 text-xs text-gray-500">
              <div className="flex items-center">
                <Clock className="w-3.5 h-3.5 mr-1" />
                {job.postedDays} days ago
              </div>
              <div className="flex items-center">
                <Users className="w-3.5 h-3.5 mr-1" />
                {job.applicants} applicants
              </div>
            </div>
            <Button 
              size="sm" 
              className="bg-[#4a90e2] hover:bg-[#3a7bc8] text-white transition-colors"
              onClick={() => navigate(`/job/${job.id}`)}
            >
              Apply Now
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default JobCard;